import jsQR from "jsqr";

/**
 * Rear-camera QR reader for the offline code swap. Uses the browser's BarcodeDetector where it exists (Android Chrome)
 * and jsQR elsewhere (iPhone Safari). An active camera also makes the browser share real local addresses with WebRTC
 * instead of `.local` names, which keeps hotspot connections working where multicast name lookup does not.
 */
export interface Scanner {
    video: HTMLVideoElement;
    stop(): void;
}

type Detector = { detect(source: CanvasImageSource): Promise<{ rawValue: string }[]> };

export async function startScanner(onText: (text: string) => void): Promise<Scanner> {
    if (!navigator.mediaDevices?.getUserMedia)
        throw new Error("This browser cannot use the camera here. Open the game over https.");
    const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } }, audio: false });
    const video = document.createElement("video");
    video.playsInline = true;
    video.muted = true;
    video.srcObject = stream;
    await video.play().catch(() => {});
    const BarcodeDetector = (globalThis as { BarcodeDetector?: new (options: { formats: string[] }) => Detector }).BarcodeDetector;
    let detector: Detector | null = null;
    try { if (BarcodeDetector) detector = new BarcodeDetector({ formats: ["qr_code"] }); } catch { detector = null; }
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d", { willReadFrequently: true })!;
    let stopped = false;
    let timer = 0;
    const read = async () => {
        if (stopped) return;
        try {
            if (video.readyState >= 2 && video.videoWidth) {
                let text: string | undefined;
                if (detector) text = (await detector.detect(video))[0]?.rawValue;
                else {
                    const scale = Math.min(1, 640 / video.videoWidth);
                    canvas.width = Math.round(video.videoWidth * scale);
                    canvas.height = Math.round(video.videoHeight * scale);
                    context.drawImage(video, 0, 0, canvas.width, canvas.height);
                    const image = context.getImageData(0, 0, canvas.width, canvas.height);
                    text = jsQR(image.data, image.width, image.height, { inversionAttempts: "dontInvert" })?.data;
                }
                if (text && !stopped) onText(text);
            }
        } catch { /* a frame that cannot be read is skipped */ }
        if (!stopped) timer = window.setTimeout(read, 180);
    };
    void read();
    return {
        video,
        stop() {
            stopped = true;
            clearTimeout(timer);
            for (const track of stream.getTracks()) track.stop();
            video.srcObject = null;
        },
    };
}
